import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
// Components
import { PrimaryButton, SecondaryButton } from "../components/Buttons";
// Custom hooks
import {
  trackError,
  useGlobal,
  trackEvent,
  usePlayer,
  useAccount,
} from "@considr-it/storied-shared";
import { useDocument } from "./use-document";
import { useTimer } from "use-timer";
import { useMicRecorder } from "./use-mic-recorder";
import { useConfirmModal } from "./use-confirm-modal";
// Constants
import { LiveQuestion, LiveStory, Topic } from "@considr-it/storied-entities";
import {
  CustomStartType,
  PaymentTier,
  RecordingObjectType,
  TTSType,
} from "@considr-it/storied-enums";
// Utils
import { timeString } from "@considr-it/storied-utils";
import { useToast } from "./use-toast";
import { File2Icon } from "../components/IconsSvg";
import { usePayment } from "./use-payment";

export const useMakeRecord = (customStartType: CustomStartType) => {
  const stopOnNoAudioDetectedRef = useRef<NodeJS.Timeout>(null);
  const maliciousCheckRef = useRef<number>(1);

  const { failureToast, dismissToasts } = useToast(false);
  const [initialisingWritingFeedback, setInitialisingWritingFeedback] =
    useState<boolean>(false);

  const navigate = useNavigate();

  // State
  const [isInitialisingLiveStory, setIsInitialisingLiveStory] =
    useState<boolean>(false);
  const [isGeneratingQuestion, setIsGeneratingQuestion] =
    useState<boolean>(false);

  const { account, login } = useAccount();
  const { paymentInfo } = usePayment();

  const [liveTranscript, setLiveTranscript] = useState<string>("");
  const [currentLiveQuestion, setCurrentLiveQuestion] =
    useState<Partial<LiveQuestion>>(null);
  const [currentLiveStory, setCurrentLiveStory] = useState<LiveStory>(null);
  const liveStoryRef = useRef<LiveStory>(null);

  // Refs
  const currentTranscriptChunk = useRef<string>("");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Hooks
  const { onPresentConfirmModal, onDismiss } = useConfirmModal();
  const { transport, isPWA, appVisibility, isMobile } = useGlobal();

  const {
    time,
    start: startTimer,
    pause: pauseTimer,
    reset: resetTimer,
  } = useTimer();

  const {
    start,
    stop,
    pause,
    isRecording,
    isPausedRecording,
    defaultAudioDevice,

    soundDetected,
  } = useMicRecorder();

  const {
    controls: { play: playQuestionAudio, stop: stopQuestionAudio },
    state: { canPlay: canPlayQuestionAudio },
    setup: setupQuestionAudio,
    reset: resetQuestionAudio,
  } = usePlayer();

  const [hasRequestedQuestion, setHasRequestedQuestion] =
    useState<boolean>(false);
  const { setCurrentTopicId, topicsCount, refreshTopicsCount } = useDocument();

  const startRecording = async (
    payload: {
      isDraft: boolean;
      outline: string;
    } = { isDraft: false, outline: "" }
  ) => {
    if (account.isAnonymous) {
      onPresentConfirmModal({
        confirmationMessage: "To proceed, sign up or log in",
        description: `Create a new account or log in`,
        actions: [
          <div>
            <PrimaryButton fullWidth onClick={() => login("signup")}>
              Sign Up
            </PrimaryButton>
            <div style={{ height: 16 }} />
            <SecondaryButton fullWidth onClick={() => login("login")}>
              Log In
            </SecondaryButton>
          </div>,
        ],
        withCloseBtn: false,
        disableOnHide: true,
      });

      return;
    }

    if (!!currentLiveQuestion?.spokenQuestion) {
      currentTranscriptChunk.current = "";
    }

    setLiveTranscript("");

    if (!isPausedRecording) {
      const navBarInstance = document.getElementById("bottom-nav-bar");
      navBarInstance?.classList.add("fade-out");

      await setupQuestionAudio({
        itemId: "question.mp3",
        itemModel: "local",
      });

      if (!currentLiveStory) {
        setIsInitialisingLiveStory(true);
        const { data } = await transport.post<LiveStory>("/createLiveStory", {
          customStartType,
          ...payload,
        });

        setCurrentLiveStory(data);
        setIsInitialisingLiveStory(false);
      }

      currentTranscriptChunk.current = "";
    } else {
      await resetQuestionAudio();
    }

    await start(
      RecordingObjectType.TOPIC,
      onEnd,
      onTranscribe,
      canvasRef.current
    );

    startTimer();
  };

  const pauseRecording = async (forcedToBackground = false) => {
    pauseTimer();

    await resetQuestionAudio();

    setCurrentLiveQuestion(null);
    setIsGeneratingQuestion(true);
    const transcript = await pause();
    if (!!transcript) {
      currentTranscriptChunk.current += `${transcript}\n`;
      try {
        if (transcript?.split(" ")?.length >= 20 && topicsCount === 0) {
          window.gtag("config", "AW-11133507454");
          window.gtag("event", "conversion", {
            send_to: "AW-11133507454/Bo4iCLfbmMkZEP6u77wp",
          });
        }

        const resp = await transport.post<{
          liveQuestion: LiveQuestion;
          liveStory: LiveStory;
        }>("/addTranscriptToLiveStory", {
          liveStoryId: currentLiveStory.id,
          subTranscript: transcript,
          noQuestion: forcedToBackground,
        });

        if (resp?.data) {
          const { liveQuestion, liveStory } = resp.data;

          if (liveQuestion) {
            await handleNewLiveQuestion(liveQuestion);
          }

          if (liveStory) {
            setCurrentLiveStory(liveStory);
          }
        }
      } catch (err) {
        trackError("failed_add_transcript_live_story", err);
      }
    }
    setIsGeneratingQuestion(false);
  };

  const getQuestion = async () => {
    await resetQuestionAudio();

    setCurrentLiveQuestion(null);
    setIsGeneratingQuestion(true);
    try {
      const { data: liveQuestion } = await transport.post<LiveQuestion>(
        "/getLiveQuestion",
        {
          liveStoryId: currentLiveStory.id,
        }
      );

      await handleNewLiveQuestion(liveQuestion);
    } catch (err) {
      trackError("failed_get_new_question", err);
    }
    setIsGeneratingQuestion(false);
  };

  const stopRecording = async () => {
    setInitialisingWritingFeedback(true);
    await resetQuestionAudio();
    await stop();
  };

  // Internal functions
  const handleNewLiveQuestion = async (liveQuestion: LiveQuestion) => {
    if (liveQuestion.spokenQuestion) {
      setHasRequestedQuestion(true);

      await playQuestionAudio({
        itemId: liveQuestion.id,
        itemModel: TTSType.LiveQuestion,
        isTemporary: true,
      });
    }
    setLiveTranscript("");
    setCurrentLiveQuestion(liveQuestion);
  };

  const onEnd = async (objectId: string) => {
    const { data: topic } = await transport.post<Topic>("/createTopic", {
      topicId: objectId,
      liveStoryId: liveStoryRef.current.id,
    });

    setCurrentTopicId(topic.id);

    await refreshTopicsCount();

    const navBarInstance = document.getElementById("bottom-nav-bar");

    if (navBarInstance) {
      navBarInstance.classList.remove("fade-out");
    }

    navigate(`topic/${topic.id}`);
  };

  const onTranscribe = async (transcript: string) => {
    setLiveTranscript(transcript);
  };

  const cleanupRecordingFlow = () => {
    setCurrentLiveStory(null);
    setCurrentLiveQuestion(null);

    resetTimer();

    resetQuestionAudio();
    stop(true);
    canvasRef.current = null;
  };

  useEffect(() => {
    if (isRecording && liveTranscript?.length > 0 && currentLiveStory) {
      const words = liveTranscript.split(" ");
      const threshold = maliciousCheckRef.current * 30;

      if (words.length >= threshold) {
        transport
          .post<{ malicious: boolean }>("/maliciousCheck", {
            liveTranscript,
            liveStoryId: currentLiveStory.id,
          })
          .then((resp) => {
            if (resp.data.malicious) {
              window.location.reload();
            }
          });

        maliciousCheckRef.current++;
      }
    }
  }, [liveTranscript, currentLiveStory, isRecording]);

  useEffect(() => {
    if (currentLiveStory) {
      liveStoryRef.current = currentLiveStory;
    } else {
      liveStoryRef.current = null;
    }
  }, [currentLiveStory]);

  useEffect(() => {
    if (isRecording && !defaultAudioDevice) {
      pauseRecording(true);
    }
  }, [defaultAudioDevice, isRecording]);

  useEffect(() => {
    if (isRecording && isPWA && isMobile && !appVisibility) {
      trackEvent("force_pause_recording");
      pauseRecording(true);

      onPresentConfirmModal({
        confirmationMessage:
          "If you go away from the app or lock your phone your recording will be paused.\nYou can adjust this by going to Settings > Display & Brightness > Auto-Lock, then choose a longer length of time.",
        actions: [<SecondaryButton onClick={onDismiss}>Close</SecondaryButton>],
      });
    }
  }, [isPWA, appVisibility, isRecording, isMobile]);

  useEffect(() => {
    if (
      currentLiveStory &&
      !currentLiveStory.docType &&
      !currentLiveStory.customStartType &&
      currentLiveStory.subTranscripts?.length > 0 &&
      isPausedRecording
    ) {
      failureToast(
        "We can't tell what you want to create. Can you describe the writing style or document type?",
        {
          position: "top-center",
          icon: <File2Icon />,
        }
      );
    } else {
      dismissToasts();
    }
  }, [isRecording, currentLiveStory]);

  useEffect(() => {
    if (
      time > 600 &&
      paymentInfo.paymentTier === PaymentTier.PAYMENT_TIER_PRO_TRIAL &&
      isRecording
    ) {
      stopRecording();
    }
  }, [time, paymentInfo, isRecording]);

  useEffect(() => {
    if (!isRecording || soundDetected) {
      if (stopOnNoAudioDetectedRef.current) {
        clearTimeout(stopOnNoAudioDetectedRef.current);
        stopOnNoAudioDetectedRef.current = null;
      }
    } else if (!stopOnNoAudioDetectedRef.current) {
      stopOnNoAudioDetectedRef.current = setTimeout(() => {
        pauseRecording();
      }, 1000 * 60);
    }
  }, [isRecording, soundDetected]);

  return {
    isInitialisingLiveStory,
    setIsInitialisingLiveStory,

    isGeneratingQuestion,
    canPlayQuestionAudio,
    liveTranscript,
    time: timeString(time),
    currentTranscriptChunk,
    currentLiveQuestion,
    setCurrentLiveQuestion,

    canvasRef,
    currentLiveStory,
    setCurrentLiveStory,
    stopRecording,
    getQuestion,
    pauseRecording,
    startRecording,
    playQuestionAudio,
    stopQuestionAudio,

    hasRequestedQuestion,

    cleanupRecordingFlow,

    initialisingWritingFeedback,
    setInitialisingWritingFeedback,
  };
};
